import axios from 'axios';
import { api } from '@/config/api';

const PUBLIC_BASE = process.env.REACT_APP_API_BASE ?? '';

/**
 * Lightweight subset of WebForm exposed to public visitors. Backend strips
 * tenant-only fields (companyUid, submitCount etc.) before returning.
 */
export interface PublicWebForm {
  formUid: string;
  name: string;
  description?: string;
  fields: WebFormField[];
  isActive: boolean;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WebFormField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select';
  required?: boolean;
  options?: string[];
}

export interface WebForm {
  _id: string;
  formUid: string;
  companyUid: string;
  name: string;
  description?: string;
  fields: WebFormField[];
  defaultBotFlowUid?: string;
  greetingTemplateUid?: string;
  isActive: boolean;
  submitCount: number;
  lastSubmittedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWebFormPayload {
  name: string;
  description?: string;
  fields?: WebFormField[];
  defaultBotFlowUid?: string;
  greetingTemplateUid?: string;
  isActive?: boolean;
}

export interface UpdateWebFormPayload {
  name?: string;
  description?: string;
  fields?: WebFormField[];
  defaultBotFlowUid?: string;
  greetingTemplateUid?: string;
  isActive?: boolean;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const webFormsService = {
  list: async (): Promise<WebForm[]> =>
    (await api.get('/slighter_crm/web-forms')).data,

  get: async (formUid: string): Promise<WebForm> =>
    (await api.get(`/slighter_crm/web-forms/${formUid}`)).data,

  create: async (payload: CreateWebFormPayload): Promise<WebForm> =>
    (await api.post('/slighter_crm/web-forms', payload)).data,

  update: async (formUid: string, payload: UpdateWebFormPayload): Promise<WebForm> =>
    (await api.patch(`/slighter_crm/web-forms/${formUid}`, payload)).data,

  remove: async (formUid: string): Promise<{ ok: boolean }> =>
    (await api.delete(`/slighter_crm/web-forms/${formUid}`)).data,

  /** Returns the public submit URL for a given form. */
  getPublicSubmitUrl: (formUid: string): string => {
    const base =
      process.env.REACT_APP_API_BASE ?? 'https://your-host.com/api/v1';
    return `${base}/public/web-forms/${formUid}/submit`;
  },

  /** Public form definition fetch (no auth header). Used by the embed widget + public renderer. */
  getPublic: async (formUid: string): Promise<PublicWebForm> =>
    (await axios.get(`${PUBLIC_BASE}/public/web-forms/${formUid}`)).data,

  /** Public submission (no auth header). */
  submitPublic: async (
    formUid: string,
    body: { fields: Record<string, string>; page_url?: string; referrer?: string; submission_id?: string },
  ): Promise<{ ok: boolean; leadUid?: string }> =>
    (await axios.post(`${PUBLIC_BASE}/public/web-forms/${formUid}/submit`, body)).data,

  /** Returns the embed snippet HTML string. */
  getEmbedSnippet: (formUid: string): string => {
    const base =
      process.env.REACT_APP_API_BASE?.replace('/api/v1', '') ?? 'https://your-host.com';
    return [
      `<div id="slighter-form"></div>`,
      `<script`,
      `  src="${base}/embed/v1.js"`,
      `  data-form-id="${formUid}"`,
      `  data-api="${base}/api/v1">`,
      `</script>`,
    ].join('\n');
  },
};
